import styled from 'styled-components';
import { Plain } from '@agendor/shared';
import { Button, Input, vars } from '@agendor/ui';
import { WarningBanner } from './Input';
import {
    OpeningHoursDraft,
    WEEKDAYS,
    WEEKDAY_LABELS,
    cloneDraft,
    displayEnd,
    outsideReference,
    subtractIntervals,
} from '../opening-hours';

const OpeningHoursList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const OpeningHoursRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 12px;
    align-items: flex-start;
    padding: 10px 0;
    border-bottom: 1px solid ${vars.dividerColor};

    &:last-child {
        border-bottom: none;
    }
`;

const DayLabelContainer = styled.div`
    min-width: 70px;
    padding-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const OpeningHoursLabel = styled.span`
    font-weight: 600;
    color: ${vars.foregroundColor};
`;

const OpeningHoursValue = styled.span`
    color: ${vars.foregroundColor};
    font-variant-numeric: tabular-nums;
`;

const ReferenceHours = styled.span`
    font-size: 0.78rem;
    font-weight: 400;
    color: #888;
    font-variant-numeric: tabular-nums;
`;

const IntervalsContainer = styled.div`
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const IntervalRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: flex-end;
`;

const TimeInputsContainer = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const TimeSeparator = styled.span`
    color: #999;
`;

const EmptyStateRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const AddButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const OutsideNote = styled(WarningBanner)`
    flex-basis: 100%;
    padding: 8px 12px;
`;

type OpeningHoursEditorProps = {
    value: OpeningHoursDraft;
    onChange: (next: OpeningHoursDraft) => void;
    disabled: boolean;
    /**
     * The establishment's hours, shown in grey under each day and used to flag the times
     * a professional's grid puts outside them. `null` while the grid edits the
     * establishment itself, which has nothing to sit inside of.
     */
    reference: OpeningHoursDraft | null;
    closedLabel: string;
};

/**
 * The weekly interval grid, Sunday first. Serves the establishment's hours and a
 * professional's alike; `reference` and `closedLabel` are what tell the two apart.
 */
export function OpeningHoursEditor({ value, onChange, disabled, reference, closedLabel }: OpeningHoursEditorProps) {
    const changeTime = (weekday: Plain.WeekdayName, index: number, field: 'start' | 'end', raw: string) => {
        // Convert 23:59 to 24:00 for end times (UI → state)
        const time = field === 'end' && raw === '23:59' ? '24:00' : raw;
        const next = cloneDraft(value);
        next[weekday][index] = { ...next[weekday][index], [field]: time };
        onChange(next);
    };

    const addInterval = (weekday: Plain.WeekdayName) => {
        const next = cloneDraft(value);
        next[weekday].push({ start: '09:00', end: '18:00' });
        onChange(next);
    };

    const removeInterval = (weekday: Plain.WeekdayName, index: number) => {
        const next = cloneDraft(value);
        next[weekday].splice(index, 1);
        onChange(next);
    };

    const noFlags = { start: false, end: false };

    return (
        <OpeningHoursList>
            {WEEKDAYS.map((weekday) => {
                const intervals = value[weekday];
                const referenceIntervals = reference === null ? [] : reference[weekday];
                const uncovered = reference === null ? [] : subtractIntervals(intervals, referenceIntervals);

                return (
                    <OpeningHoursRow key={weekday} data-testid={`opening-hours-row-${weekday}`}>
                        <DayLabelContainer>
                            <OpeningHoursLabel>{WEEKDAY_LABELS[weekday]}</OpeningHoursLabel>
                            {reference !== null && (
                                <ReferenceHours>
                                    {referenceIntervals.length === 0
                                        ? 'Fechado'
                                        : referenceIntervals
                                              .map((interval) => `${interval.start}-${displayEnd(interval.end)}`)
                                              .join(', ')}
                                </ReferenceHours>
                            )}
                        </DayLabelContainer>
                        <IntervalsContainer>
                            {intervals.length === 0 ? (
                                <EmptyStateRow>
                                    <OpeningHoursValue style={{ flex: 1, textAlign: 'right' }}>
                                        {closedLabel}
                                    </OpeningHoursValue>
                                    <Button
                                        type="button"
                                        iconOnly
                                        onClick={() => addInterval(weekday)}
                                        aria-label="Adicionar intervalo"
                                        disabled={disabled}
                                    >
                                        ＋
                                    </Button>
                                </EmptyStateRow>
                            ) : (
                                <>
                                    {intervals.map((interval, index) => {
                                        const flagged =
                                            reference === null
                                                ? noFlags
                                                : outsideReference(interval, referenceIntervals);
                                        return (
                                            <IntervalRow key={`${weekday}-${index}`}>
                                                <TimeInputsContainer>
                                                    <Input
                                                        type="time"
                                                        value={interval.start}
                                                        hasWarning={flagged.start}
                                                        onChange={(e) =>
                                                            changeTime(weekday, index, 'start', e.target.value)
                                                        }
                                                        style={{ maxWidth: '120px' }}
                                                        disabled={disabled}
                                                    />
                                                    <TimeSeparator>até</TimeSeparator>
                                                    <Input
                                                        type="time"
                                                        value={interval.end === '24:00' ? '23:59' : interval.end}
                                                        hasWarning={flagged.end}
                                                        onChange={(e) =>
                                                            changeTime(weekday, index, 'end', e.target.value)
                                                        }
                                                        style={{ maxWidth: '120px' }}
                                                        disabled={disabled}
                                                    />
                                                </TimeInputsContainer>
                                                <Button
                                                    type="button"
                                                    iconOnly
                                                    onClick={() => removeInterval(weekday, index)}
                                                    aria-label="Remover intervalo"
                                                    disabled={disabled}
                                                    style={{ flexShrink: 0 }}
                                                    data-testid="remove-interval-btn"
                                                >
                                                    －
                                                </Button>
                                            </IntervalRow>
                                        );
                                    })}
                                    <AddButtonContainer>
                                        <Button
                                            type="button"
                                            iconOnly
                                            onClick={() => addInterval(weekday)}
                                            aria-label="Adicionar intervalo"
                                            disabled={disabled}
                                        >
                                            ＋
                                        </Button>
                                    </AddButtonContainer>
                                </>
                            )}
                        </IntervalsContainer>
                        {uncovered.length > 0 && (
                            <OutsideNote data-testid={`opening-hours-note-${weekday}`}>
                                {referenceIntervals.length === 0
                                    ? 'Estabelecimento fechado neste dia.'
                                    : `Estabelecimento fechado ${uncovered
                                          .map((stretch) => `das ${stretch.start} às ${displayEnd(stretch.end)}`)
                                          .join(' e ')}.`}
                            </OutsideNote>
                        )}
                    </OpeningHoursRow>
                );
            })}
        </OpeningHoursList>
    );
}
